import * as CL from '@design-system/component-library';
import { useEffect, useState } from 'react';
import { useRevalidator } from 'react-router';
import { createAdminInvitation, updateContactAccessRights } from '../../common/fetch.js';
import {
  accessRightsMsg,
  confirmMsg,
  editAccessRightsMsg,
  savingTheDataFailedMsg,
  t,
} from '../../common/i18n/index.js';
import { isAdmin, isBeingInvited, isCommunityUser, isOrderApprover } from '../../common/utils/contactUtils.js';
import { ContactRole, UserProfile } from '../../generated/api/models.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { addNotification } from '../../public/site/notificationStore.js';
import { ButtonGroupForSubmitAndBack } from '../ButtonGroupForSubmitAndBack/ButtonGroupForSubmitAndBack.js';
import { LinkableAccordion } from '../LinkableAccordion/index.js';
import { AccessRightsTable } from './AccessRightsTable.js';
import { ClassicAccessRightsTable } from './ClassicAccessRightsTable.js';
import { ContactDetailsMissingDisclaimer } from './ContactDetailsMissingDisclaimer.js';
import { InvitationDisclaimer } from './InvitationDisclaimer.js';
import { InvitationRequiredModal } from './InvitationRequiredModal.js';
import { InvitationWillBeSentDisclaimer } from './InvitationWillBeSentDisclaimer.js';
import { PendingInvitationModal } from './PendingInvitationModal.js';
import type { ContactAccessRightsUpdateRequest } from '../../generated/api/contactAccessRightsUpdateRequest.js';
import type { ContactInvitation } from '../../generated/api/contactInvitation.js';
import type { GetServiceAgreementsResponse } from '../../generated/api/getServiceAgreementsResponse.js';
import type { AccountContactRelationship, AdminInvitation, Contact } from '../../generated/api/models.js';

import './AccessRights.scss';

const getAccountContactRelationships = (contact?: Contact, invitation?: ContactInvitation) => {
  if (isBeingInvited(invitation)) {
    return invitation?.noeAccessRights.accountContactRelationships ?? [];
  }
  if (isCommunityUser(contact)) {
    return contact?.accountContactRelationships ?? [];
  }
  return [];
};

const getAccountMasterIdsContactIsAdminFor = (contact?: Contact, invitation?: ContactInvitation) => {
  const accountContactRelationships = getAccountContactRelationships(contact, invitation);
  const primaryAccountMasterId = accountContactRelationships?.find(acr => acr.isPrimaryAccount)?.accountMasterId;
  const secondaryAccountMasterIds =
    accountContactRelationships?.filter(acr => !acr.isPrimaryAccount).map(acr => acr.accountMasterId) ?? [];
  if ((isAdmin(contact) || isBeingInvited(invitation)) && primaryAccountMasterId) {
    return [primaryAccountMasterId, ...secondaryAccountMasterIds];
  }
  return secondaryAccountMasterIds;
};

const getAdditionalServiceRole = (
  serviceAgreementsResponse?: GetServiceAgreementsResponse,
  invitation?: ContactInvitation
) => {
  if (isBeingInvited(invitation)) {
    return invitation?.oecAccessRights.additionalServiceRole;
  }
  return serviceAgreementsResponse?.additionalServiceRole;
};

const getServiceAgreementIds = (
  serviceAgreementsResponse?: GetServiceAgreementsResponse,
  invitation?: ContactInvitation
) => {
  if (isBeingInvited(invitation)) {
    return invitation?.oecAccessRights.saIds ?? [];
  }
  return serviceAgreementsResponse?.serviceAgreements?.map(sa => sa.saId) ?? [];
};

const getUpdatedACRs = (selectedAccountMasterIds: string[], contact?: Contact): AccountContactRelationship[] => {
  const primaryACR = (contact?.accountContactRelationships ?? []).find(acr => acr.isPrimaryAccount)!;
  const updatedSecondaryACRs: AccountContactRelationship[] = selectedAccountMasterIds
    .filter(accountMasterId => accountMasterId !== primaryACR.accountMasterId)
    .map(accountMasterId => ({
      isPrimaryAccount: false,
      accountMasterId,
    }));
  return [primaryACR, ...updatedSecondaryACRs];
};

interface AccessRightsProps {
  contact?: Contact;
  invitation?: ContactInvitation;
  serviceAgreementsResponse?: GetServiceAgreementsResponse;
  onBeforeEdit?: () => boolean;
}

export const AccessRights = ({ contact, invitation, serviceAgreementsResponse, onBeforeEdit }: AccessRightsProps) => {
  const { revalidate } = useRevalidator();
  const { authenticatedUser } = useAuth();
  const [isEdit, setIsEdit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [displayContactDetailsMissingWarning, setDisplayContactDetailsMissingWarning] = useState(false);
  const [displayPendingInvitationWarning, setDisplayPendingInvitationWarning] = useState(false);
  const [displayInvitationRequiredError, setDisplayInvitationRequiredError] = useState(false);
  const [selectedAccountMasterIds, setSelectedAccountMasterIds] = useState<string[]>(
    getAccountMasterIdsContactIsAdminFor(contact, invitation)
  );
  const [isApprover, setIsApprover] = useState(() => isOrderApprover(contact));
  const [selectedServiceAgreementIds, setSelectedServiceAgreementIds] = useState<string[]>(
    getServiceAgreementIds(serviceAgreementsResponse, invitation)
  );
  const [additionalServiceRole, setAdditionalServiceRole] = useState(() =>
    getAdditionalServiceRole(serviceAgreementsResponse, invitation)
  );

  // Required to reset the state when invitation is cancelled in InvitationDisclaimer
  useEffect(() => {
    if (contact) {
      setSelectedAccountMasterIds(getAccountMasterIdsContactIsAdminFor(contact, invitation));
      setIsApprover(isOrderApprover(contact));
      setSelectedServiceAgreementIds(getServiceAgreementIds(serviceAgreementsResponse, invitation));
      setAdditionalServiceRole(getAdditionalServiceRole(serviceAgreementsResponse, invitation));
    }
  }, [contact, invitation, serviceAgreementsResponse]);

  if (!authenticatedUser) {
    return null;
  }

  const primaryContactRelationship = contact?.accountContactRelationships?.find(rel => rel.isPrimaryAccount);
  if (!primaryContactRelationship) {
    return null;
  }
  const contactPrimaryMasterId = primaryContactRelationship!.accountMasterId;

  const updateAccessRights = async () => {
    const contactAccessRightsUpdateRequest: ContactAccessRightsUpdateRequest = {
      userProfile: selectedAccountMasterIds.length > 0 ? UserProfile.KEY_USER : UserProfile.EMPLOYEE,
      addAccountContactRelationships: selectedAccountMasterIds
        .filter(
          accountMasterId =>
            accountMasterId !== contact!.accountContactRelationships?.find(acr => acr.isPrimaryAccount)?.accountMasterId
        )
        .filter(
          accountMasterId => !contact!.accountContactRelationships?.some(acr => acr.accountMasterId === accountMasterId)
        )
        .map(accountMasterId => ({
          isPrimaryAccount: false,
          accountMasterId,
        })),
      removeAccountContactRelationships: (contact!.accountContactRelationships ?? [])
        .filter(acr => !acr.isPrimaryAccount)
        .filter(acr => !selectedAccountMasterIds?.some(accountMasterId => accountMasterId === acr.accountMasterId)),
      approver: isApprover,
      addServiceAgreementIds: selectedServiceAgreementIds.filter(
        saId => !getServiceAgreementIds(serviceAgreementsResponse).includes(saId)
      ),
      removeServiceAgreementIds: getServiceAgreementIds(serviceAgreementsResponse).filter(
        saId => !selectedServiceAgreementIds.includes(saId)
      ),
      additionalServiceRole: additionalServiceRole,
    };

    const changesMade =
      contactAccessRightsUpdateRequest.userProfile !== contact!.person?.userProfile ||
      contactAccessRightsUpdateRequest.addAccountContactRelationships.length > 0 ||
      contactAccessRightsUpdateRequest.removeAccountContactRelationships.length > 0 ||
      contactAccessRightsUpdateRequest.approver !== isOrderApprover(contact) ||
      contactAccessRightsUpdateRequest.addServiceAgreementIds.length > 0 ||
      contactAccessRightsUpdateRequest.removeServiceAgreementIds.length > 0 ||
      contactAccessRightsUpdateRequest.additionalServiceRole !== serviceAgreementsResponse?.additionalServiceRole;

    setIsSubmitting(true);
    try {
      if (changesMade) {
        await updateContactAccessRights(contact!.contactMasterId!, contactAccessRightsUpdateRequest);
        addNotification(t.FFJ2('Changes were successfully saved'), 'success');
        revalidate();
      }
      setIsEdit(false);
    } catch (err) {
      addNotification(t.RQA1(savingTheDataFailedMsg), 'warning');
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendInvitation = async () => {
    const adminInvitation: AdminInvitation = {
      recipient: contact!.contactId!,
      noeAccessRights: {
        accountContactRelationships: getUpdatedACRs(selectedAccountMasterIds, contact),
        roles: isApprover ? [ContactRole.APPROVER] : [],
      },
      oecAccessRights: {
        saIds: selectedServiceAgreementIds,
        additionalServiceRole: additionalServiceRole,
      },
    };

    setIsSubmitting(true);
    try {
      await createAdminInvitation(adminInvitation, contactPrimaryMasterId);
      addNotification(t.M16N('Invitation sent successfully'), 'success');
      revalidate();
      setIsEdit(false);
    } catch (err) {
      addNotification(t.AANC('Failed to send the invitation'), 'warning');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitContact = async () => {
    if (!contact?.contactId || !authenticatedUser?.mdmId) {
      return;
    }
    if (isCommunityUser(contact)) {
      updateAccessRights();
    } else if (selectedAccountMasterIds.length > 0) {
      sendInvitation();
    } else if (isApprover) {
      setDisplayInvitationRequiredError(true);
    }
  };

  const handleEditClick = () => {
    if (!onBeforeEdit || onBeforeEdit()) {
      if (!contact?.person?.phoneNumber || !contact?.person?.email) {
        setDisplayContactDetailsMissingWarning(true);
      } else if (isBeingInvited(invitation)) {
        setDisplayPendingInvitationWarning(true);
      } else {
        setDisplayContactDetailsMissingWarning(false);
        setDisplayPendingInvitationWarning(false);
        setIsEdit(true);
      }
    }
  };

  const primaryAccountMasterId = authenticatedUser.mdmId!;
  const secondaryAccountMasterIds = authenticatedUser?.secondaryAccounts?.map(account => account.accountMasterId) ?? [];
  const availableAccountMasterIds = [primaryAccountMasterId, ...secondaryAccountMasterIds];

  // Order Acceptor is mandatory if admin rights are selected.
  // Secondary company admin rights require primary company admin rights.
  const addAdminRight = (accountMasterId: string) => {
    if (accountMasterId !== contactPrimaryMasterId && !selectedAccountMasterIds.includes(contactPrimaryMasterId)) {
      setSelectedAccountMasterIds([...selectedAccountMasterIds, contactPrimaryMasterId, accountMasterId]);
      setIsApprover(true);
    } else {
      setSelectedAccountMasterIds([...selectedAccountMasterIds, accountMasterId]);
      if (accountMasterId === contactPrimaryMasterId) {
        setIsApprover(true);
      }
    }
  };

  // Secondary company admin rights require primary company admin rights, so removing primary company admin rights will remove all admin rights
  const removeAdminRight = (accountMasterId: string) => {
    if (accountMasterId === contactPrimaryMasterId) {
      setSelectedAccountMasterIds([]);
      setIsApprover(false);
      setSelectedServiceAgreementIds([]);
      setAdditionalServiceRole(undefined);
    } else {
      setSelectedAccountMasterIds(selectedAccountMasterIds.filter(id => id !== accountMasterId));
    }
  };

  const toggleAllAdminRights = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedAccountMasterIds(availableAccountMasterIds ?? []);
      setIsApprover(true);
    } else {
      setSelectedAccountMasterIds([]);
      setIsApprover(false);
      setSelectedServiceAgreementIds([]);
      setAdditionalServiceRole(undefined);
    }
  };

  const addServiceAgreementId = (serviceAgreementId: string) => {
    setSelectedServiceAgreementIds([...selectedServiceAgreementIds, serviceAgreementId]);
    if (!selectedAccountMasterIds.includes(contactPrimaryMasterId)) {
      setSelectedAccountMasterIds([...selectedAccountMasterIds, contactPrimaryMasterId]);
      setIsApprover(true);
    }
  };

  const removeServiceAgreementId = (serviceAgreementId: string) => {
    setSelectedServiceAgreementIds(selectedServiceAgreementIds.filter(id => id !== serviceAgreementId));
    setAdditionalServiceRole(undefined);
  };

  const toggleAllServiceAgreements = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedServiceAgreementIds(
        authenticatedUser?.oecServiceAgreements?.serviceAgreements?.map(sa => sa.saId) ?? []
      );
      if (!selectedAccountMasterIds.includes(contactPrimaryMasterId)) {
        setSelectedAccountMasterIds([...selectedAccountMasterIds, contactPrimaryMasterId]);
        setIsApprover(true);
      }
    } else {
      setSelectedServiceAgreementIds([]);
      setAdditionalServiceRole(undefined);
    }
  };

  return (
    <div className="of-contact-access-rights">
      {displayInvitationRequiredError && (
        <InvitationRequiredModal onClose={() => setDisplayInvitationRequiredError(false)} />
      )}
      {displayPendingInvitationWarning && (
        <PendingInvitationModal onClose={() => setDisplayPendingInvitationWarning(false)} />
      )}
      <div className="of-contact-access-rights__content">
        <InvitationDisclaimer invitation={invitation} contactEmail={contact?.person?.email} />
        {isEdit || selectedAccountMasterIds.length > 0 ? (
          <>
            <AccessRightsTable
              contactPrimaryMasterId={contactPrimaryMasterId}
              invitation={invitation}
              isEdit={isEdit}
              isApprover={isApprover}
              isCommunityUser={isCommunityUser(contact)}
              setIsApprover={setIsApprover}
              selectedAccountMasterIds={selectedAccountMasterIds}
              addAdminRight={addAdminRight}
              removeAdminRight={removeAdminRight}
              toggleAllAdminRights={toggleAllAdminRights}
            />
            <ClassicAccessRightsTable
              invitation={invitation}
              isEdit={isEdit}
              additionalServiceRole={additionalServiceRole}
              setAdditionalServiceRole={setAdditionalServiceRole}
              selectedServiceAgreementIds={selectedServiceAgreementIds}
              addServiceAgreementId={addServiceAgreementId}
              removeServiceAgreementId={removeServiceAgreementId}
              toggleAllServiceAgreements={toggleAllServiceAgreements}
            />
          </>
        ) : null}
        {!isEdit && selectedAccountMasterIds.length === 0 && (
          <div>{t.OKJR('No access rights have been granted to this user.')}</div>
        )}
        {isEdit && !isCommunityUser(contact) && (selectedAccountMasterIds.length > 0 || isApprover) && (
          <InvitationWillBeSentDisclaimer />
        )}
      </div>
      <div className="of-contact-access-rights__buttons">
        {isEdit && contact && (
          <ButtonGroupForSubmitAndBack
            onCancel={() => {
              setSelectedAccountMasterIds(getAccountMasterIdsContactIsAdminFor(contact));
              setIsApprover(isOrderApprover(contact));
              setSelectedServiceAgreementIds(getServiceAgreementIds(serviceAgreementsResponse, invitation));
              setAdditionalServiceRole(getAdditionalServiceRole(serviceAgreementsResponse, invitation));
              setIsEdit(false);
            }}
            onSubmit={submitContact}
            submitButtonText={t.QVYK(confirmMsg)}
            submitting={isSubmitting}
            className="of-contact-user-rights-accordion__button-group"
            submitOnLeft={true}
          />
        )}
        {!isEdit && (
          <CL.Button type="button" color="light" onClick={handleEditClick}>
            {t.SDO3(editAccessRightsMsg)}
          </CL.Button>
        )}
      </div>
      {displayContactDetailsMissingWarning && (
        <ContactDetailsMissingDisclaimer onClose={() => setDisplayContactDetailsMissingWarning(false)} />
      )}
    </div>
  );
};

export const AccessRightsAccordion = (props: AccessRightsProps) => (
  <LinkableAccordion id="access-rights-accordion" heading={t.Y8LP(accessRightsMsg)}>
    <AccessRights {...props} />
  </LinkableAccordion>
);
